// InvitacionModal — muestra el link de signup recién generado (o regenerado)
// para que el kine lo copie / mande por WhatsApp al paciente.
//
// Sprint 3 B5. Sub-decisión Lock Ale 2026-06-28 Q5: NO email automático
// (sin SMTP config para piloto). Kine envía manual via WhatsApp o copiar
// link.

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/app/components/ui/dialog";
import { Button } from "@/app/components/ui/button";
import { Copy, Check, MessageCircle } from "lucide-react";
import {
  getPacienteSignupLink,
  getInvitacionWhatsappText,
  getWhatsappShareUrl,
} from "@/lib/auth/signupLink";

interface InvitacionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Token de invitación recién generado o existente. */
  signupToken: string;
  /** Para armar el saludo y el WhatsApp link si hay telefono. */
  pacienteNombre: string;
  pacienteTelefono?: string | null;
  /** ISO datetime — para mostrar "vence en X días". */
  expiresAt: string;
  /** Variant cambia el copy del título: "creada" post-create, "actualizada"
   *  post-regenerate, "actual" cuando solo copia link existente. */
  variant?: "creada" | "actualizada" | "actual";
}

export function InvitacionModal({
  open,
  onOpenChange,
  signupToken,
  pacienteNombre,
  pacienteTelefono,
  expiresAt,
  variant = "creada",
}: InvitacionModalProps) {
  const link = getPacienteSignupLink(signupToken);
  const whatsappText = getInvitacionWhatsappText(pacienteNombre, link);
  const whatsappUrl = getWhatsappShareUrl(whatsappText, pacienteTelefono);

  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.warn("[InvitacionModal] clipboard write failed", e);
    }
  };

  const title =
    variant === "creada"
      ? "Invitación creada"
      : variant === "actualizada"
        ? "Invitación renovada"
        : "Link de invitación";

  const subtitle =
    variant === "creada"
      ? `${pacienteNombre} fue creado. Enviale el link para que active su cuenta.`
      : variant === "actualizada"
        ? `Generamos un link nuevo para ${pacienteNombre}.`
        : `El link de ${pacienteNombre} sigue activo.`;

  const expiryLabel = formatExpiryRelative(expiresAt);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground mb-2">{subtitle}</p>

        <div className="bg-muted/30 rounded-lg border border-border p-3 break-all text-sm font-mono">
          {link}
        </div>

        <p className="text-xs text-muted-foreground -mt-1">
          Vence {expiryLabel}.
        </p>

        <div className="grid grid-cols-2 gap-2 mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleCopy}
            className="w-full"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                ¡Copiado!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copiar link
              </>
            )}
          </Button>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full"
          >
            <Button type="button" className="w-full">
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </Button>
          </a>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "en X días" / "mañana" / "hoy" / "vencido". */
function formatExpiryRelative(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return "vencido";
  const days = Math.floor(ms / (24 * 3600 * 1000));
  if (days === 0) return "hoy";
  if (days === 1) return "mañana";
  return `en ${days} días`;
}
